import type {
  ExportAnnotation,
  ExportHighlightAnnotation,
  ExportInkAnnotation,
} from '../schemas/export.js';
import type { ExportModel } from './build-export-model.js';

export type { ExportAnnotation, ExportHighlightAnnotation, ExportInkAnnotation };
import type { TilePage } from './compute-tile-pages.js';
import { DEFAULT_COLUMN_WIDTH_PX, DEFAULT_ROW_HEIGHT_PX } from './page-geometry.js';

/**
 * Export annotations — decisions A1-A3 (2026-08-25 doc).
 *
 * **Why not page coordinates.** A pdf.js annotation is a stroke at (x, y) on
 * page N. Every remaining customisation option — orientation, scale preset,
 * column/dimension selection, the subject block, header repetition — changes
 * page count, page size, or where a given cell sits. The only option that
 * moves nothing is the theme. So a page-anchored stroke is meaningless one
 * option-change later: it lands on unrelated content, or on a page that no
 * longer exists. Anchoring to matrix *content* is the only model in which
 * "annotations persist across option changes" is a promise rather than a
 * hope.
 *
 * That choice pays a second dividend: because an annotation is data about the
 * matrix rather than marks on a rendered artifact, it can travel in the export
 * request and be baked into the document by the same renderer that draws the
 * grid. "The export is exactly what the viewer shows" then holds *by
 * construction* — there is no second artifact to reconcile.
 */

/**
 * The annotation shapes themselves are defined (and Zod-validated) in
 * `schemas/export.ts`, because they cross the wire. Their design rationale
 * lives there and here:
 *
 * **Ink** (A2) anchors its first point to a cell as a fraction of that cell's
 * box and stores the rest of the path as offsets in *unscaled grid pixels*.
 * Accepted limitation, recorded deliberately: the path is rigid, so under a
 * column or dimension toggle the stroke keeps its shape and origin but its tail
 * no longer tracks the content it was drawn over — right for circling a cell,
 * imperfect for a long sweep. Per-point anchoring was considered and rejected
 * for its data weight (a few hundred anchored points per stroke).
 *
 * **Highlight** (A3) anchors to a content line and needs no projection at all,
 * which is the whole advantage over a rectangle: a line's identity is
 * independent of scale, orientation, tiling and reflow, so the renderer only
 * has to ask "is this line highlighted?".
 */

/** Stable key for a highlighted line — `rowKey:columnNumber:lineIndex`. */
export function highlightKey(annotation: ExportHighlightAnnotation): string {
  return `${annotation.rowKey}:${annotation.columnNumber}:${annotation.lineIndex}`;
}

/** The set of highlighted line keys, for the renderer to consult per line. */
export function highlightedLineKeys(annotations: readonly ExportAnnotation[]): Set<string> {
  const keys = new Set<string>();
  for (const annotation of annotations) {
    if (annotation.kind === 'highlight') keys.add(highlightKey(annotation));
  }
  return keys;
}

/** One projected path point, in scaled pixels relative to a tile's content-area origin. */
export interface ProjectedPoint {
  x: number;
  y: number;
}

/**
 * Where an ink stroke lands for a given render.
 *
 * Coordinates are relative to the top-left of the tile's **content area** —
 * that is, excluding the fixed-width row-label column(s) and excluding the
 * column-header row. Both of those are deliberately outside this frame: the
 * label columns are a known constant the caller can add, while the header
 * row's real height is decided by CSS at layout time (`HEADER_ROW_RESERVED_PX`
 * is a conservative reservation for bin-packing, not a measurement). Keeping
 * the pure math to the part that *is* deterministic means the overlay can add
 * the one offset it can actually measure, instead of this function guessing.
 */
export interface ProjectedInk {
  /** Index into the `tiles` array passed in. */
  tileIndex: number;
  points: ProjectedPoint[];
}

function columnWidth(columnNumber: number, columnWidths: Record<string, number>): number {
  return columnWidths[String(columnNumber)] ?? DEFAULT_COLUMN_WIDTH_PX;
}

function rowHeight(row: ExportModel['rows'][number], rowHeights: Record<string, number>): number {
  return rowHeights[`${row.kind}:${row.key}`] ?? DEFAULT_ROW_HEIGHT_PX;
}

/**
 * Re-projects a stroke onto whatever layout the current options produce, or
 * returns null when its anchor cell is not in this export at all (the column
 * was unticked, the dimension removed) — in which case the stroke correctly
 * disappears with the content it was drawn on.
 */
export function projectInkAnnotation(input: {
  annotation: ExportInkAnnotation;
  model: ExportModel;
  tiles: readonly TilePage[];
  columnWidths: Record<string, number>;
  rowHeights: Record<string, number>;
}): ProjectedInk | null {
  const { annotation, model, tiles, columnWidths, rowHeights } = input;

  const columnIndex = model.columns.findIndex(
    (column) => column.number === annotation.columnNumber,
  );
  const rowIndex = model.rows.findIndex((row) => row.key === annotation.rowKey);
  if (columnIndex === -1 || rowIndex === -1) return null;

  const tileIndex = tiles.findIndex(
    (tile) => tile.columnIndices.includes(columnIndex) && tile.rowIndices.includes(rowIndex),
  );
  if (tileIndex === -1) return null;
  const tile = tiles[tileIndex];
  if (tile === undefined) return null;

  const scale = tile.scalePercent / 100;

  // Offset of the anchor cell inside this tile's content area, unscaled: sum
  // the sizes of the columns/rows that precede it *on this tile*, not in the
  // whole model — a tile past the first starts its own coordinate space.
  let cellLeft = 0;
  for (const index of tile.columnIndices) {
    if (index === columnIndex) break;
    const column = model.columns[index];
    if (column !== undefined) cellLeft += columnWidth(column.number, columnWidths);
  }
  let cellTop = 0;
  for (const index of tile.rowIndices) {
    if (index === rowIndex) break;
    const row = model.rows[index];
    if (row !== undefined) cellTop += rowHeight(row, rowHeights);
  }

  const anchorColumn = model.columns[columnIndex];
  const anchorRow = model.rows[rowIndex];
  if (anchorColumn === undefined || anchorRow === undefined) return null;
  const cellWidth = columnWidth(anchorColumn.number, columnWidths);
  const cellHeight = rowHeight(anchorRow, rowHeights);

  const originLeft = cellLeft + annotation.originX * cellWidth;
  const originTop = cellTop + annotation.originY * cellHeight;

  return {
    tileIndex,
    points: annotation.points.map((point) => ({
      x: (originLeft + point.dx) * scale,
      y: (originTop + point.dy) * scale,
    })),
  };
}

/** Projects every ink stroke, dropping those whose anchor cell left the export. */
export function projectInkAnnotations(input: {
  annotations: readonly ExportAnnotation[];
  model: ExportModel;
  tiles: readonly TilePage[];
  columnWidths: Record<string, number>;
  rowHeights: Record<string, number>;
}): ProjectedInk[] {
  const projected: ProjectedInk[] = [];
  for (const annotation of input.annotations) {
    if (annotation.kind !== 'ink') continue;
    const result = projectInkAnnotation({ ...input, annotation });
    if (result !== null) projected.push(result);
  }
  return projected;
}
